import { useState } from 'react';
import { Alert, Box, Button, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import axios from 'axios';
import useSWR from 'swr';

import {
  disconnectMcpConnection,
  fetchMcpConnections,
  mcpConnectionAccessLabel,
} from '../../features/mcpConnections';
import { connectionSessionRestriction } from '../../lib/chatGptConnection';
import { useAuthStore } from '../../store/authStore';
import { ChatGptSetup } from './ChatGptSetup';

export function ConnectionsSettings() {
  const user = useAuthStore((state) => state.user);
  const sessionKind = useAuthStore((state) => state.sessionKind);
  const supportSession = useAuthStore((state) => state.supportSession);
  const restriction = connectionSessionRestriction({ sessionKind, supportSession });
  const { data, error, isLoading, mutate } = useSWR(
    user && !restriction ? ['mcp-connections', user.id] : null,
    fetchMcpConnections,
    { shouldRetryOnError: false },
  );
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  async function disconnect(id: string, name: string) {
    if (disconnectingId) return;
    setDisconnectingId(id);
    setActionError(null);
    setSuccess(null);
    try {
      await disconnectMcpConnection(id);
      await mutate((current) => current?.filter((connection) => connection.id !== id), {
        revalidate: false,
      });
      setSuccess(`${name} has been disconnected. Its access to your Paddiq data has ended.`);
    } catch {
      setActionError('Could not disconnect this app. Please try again.');
    } finally {
      setDisconnectingId(null);
    }
  }

  if (restriction) return <Alert severity="warning">{restriction}</Alert>;

  return (
    <Stack spacing={2}>
      {user ? <ChatGptSetup key={user.id} userId={user.id} /> : null}
      <Divider />
      <Box>
        <Typography component="h2" variant="h6" fontWeight={800}>
          Connected apps
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Active Paddiq access grants for your account. This does not check whether ChatGPT is
          currently connected.
        </Typography>
      </Box>
      <Button sx={{ alignSelf: 'flex-start' }} onClick={() => void mutate()}>
        Refresh access
      </Button>
      {isLoading ? <CircularProgress size={24} aria-label="Loading connected apps" /> : null}
      {error ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={() => void mutate()}>
              Retry
            </Button>
          }
        >
          {axios.isAxiosError(error) && error.response?.status === 404
            ? 'App connections are not enabled for this Paddiq service.'
            : 'Could not load connected apps.'}
        </Alert>
      ) : null}
      {actionError ? <Alert severity="error">{actionError}</Alert> : null}
      {success ? <Alert severity="success">{success}</Alert> : null}
      {data?.length === 0 ? (
        <Typography color="text.secondary">
          No active Paddiq access grants. Complete sign-in and consent in ChatGPT, then refresh
          access.
        </Typography>
      ) : null}
      {data?.map((connection) => (
        <Box
          key={connection.id}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}
        >
          <Stack spacing={1.5}>
            <Box>
              <Typography fontWeight={700}>{connection.clientName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {mcpConnectionAccessLabel(connection.scope)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Authorized {new Date(connection.createdAt).toLocaleDateString()} · Expires{' '}
                {new Date(connection.expiresAt).toLocaleDateString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Disconnect ends this grant’s access immediately. Remove the plugin in ChatGPT
                separately if you no longer want to use it.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="error"
              sx={{ alignSelf: 'flex-start' }}
              disabled={disconnectingId !== null}
              onClick={() => void disconnect(connection.id, connection.clientName)}
            >
              {disconnectingId === connection.id ? 'Disconnecting…' : 'Disconnect'}
            </Button>
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}
